import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { ApiError } from './models';
import { Axios } from './axios';
import { decodeToken } from '../jwt';

interface AuthTokens {
  refreshToken: string;
  accessToken: string;
}

interface RefreshTokenRequest {
  token: string;
}

/**
 * @class Api Class is a fancy es6 wrapper class for axios.
 *
 * @param {import("axios").AxiosRequestConfig} config - axios Request Config.
 * @link [AxiosRequestConfig](https://github.com/axios/axios#request-config)
 */

export class Api extends Axios {
  private token: string;

  /**
   * Creates an instance of api.
   * @param {import("axios").AxiosRequestConfig} conf
   */
  public constructor(conf: AxiosRequestConfig) {
    super(conf);

    this.token = '';

    this.interceptors.request.use(
      (config) => {
        const newConfig = config;

        if (newConfig.baseURL?.startsWith('./') && !newConfig.url?.startsWith('http')) {
          newConfig.url = `data/${config.url}_${config.method}.json`;
          newConfig.method = 'get';
        }

        const accessToken = localStorage.getItem('accessToken');
        if (typeof accessToken === 'string' && newConfig.headers) {
          // add request interceptors, like token, custom headers,...
          newConfig.headers.authorization = `Bearer ${accessToken}`;

          // check if exp. time is in less then 15 min.
          const expDate = decodeToken(accessToken).exp;
          const now: number = new Date().getTime() / 1e3;
          const rt = localStorage.getItem('refreshToken');
          const diff = expDate - now;

          if (diff < 7200 && diff > 0 && rt && config.url?.indexOf('refresh-token') === -1) {
            //  AccountService.refreshToken(rt);
            const response = this.post<AuthTokens, RefreshTokenRequest>('refresh-token', {
              token: rt,
            });
            response.then((tokens) => {
              localStorage.setItem('accessToken', tokens.data.accessToken);
              localStorage.setItem('refreshToken', tokens.data.refreshToken);
            });
          }
        }

        return newConfig;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // this middleware is been called right before the response is get it by the method that triggers the request
    this.interceptors.response.use(
      (response) => {
        return response.data ? response.data : response;
      },
      (error) => {
        if (error) {
          if (axios.isCancel(error) || !error.response) {
            return Promise.reject(error);
          }
          const { data } = error.response;
          switch (error.response.status) {
            case 400:
              if (data && data.error) {
                if (data.error.validation) {
                  const key = Object.keys(data.error.validation)[0];
                  const value = Object.values(data.error.validation)[0] as string;
                  if (key && value) {
                    return Promise.reject(new ApiError(`API-VALIDATION.${key.toUpperCase()}.${value.toUpperCase()}`));
                  }
                } // validation end
                return Promise.reject(new ApiError(data.error.message, data.error.code, data.error.cause));
              }
              break;

            case 401:
              if (error.response.config?.url === 'login') {
                return Promise.reject(new ApiError(data.error.message, data.error.code, data.error.cause));
              }

              localStorage.clear();
              setTimeout(() => {
                window.history.pushState(undefined, '', '/');
                window.location.reload();
              }, 350);
              break;

            default:
              break;
          }
          if (data && data.error) {
            return Promise.reject(new ApiError(data.error.message, data.error.code, data.error.cause));
          }
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Gets Token.
   *
   * @returns {string} token.
   * @memberof Api
   */
  public getToken(): string {
    return `Bearer ${this.token}`;
  }

  /**
   * Sets Token.
   *
   * @param {string} token - token.
   * @memberof Api
   */
  public setToken(token: string): void {
    this.token = token;
  }

  /**
   * Get Uri
   *
   * @param {import("axios").AxiosRequestConfig} [config]
   * @returns {string}
   * @memberof Api
   */
  public getUri(config?: AxiosRequestConfig): string {
    return this.getUri(config);
  }

  /**
   * Generic request.
   *
   * @access public
   * @template T - `TYPE`: expected object.
   * @template R - `RESPONSE`: expected object inside a axios response format.
   * @param {import("axios").AxiosRequestConfig} [config] - axios request configuration.
   * @returns {Promise<R>} - HTTP axios response payload.
   * @memberof Api
   *
   * @example
   * api.request({
   *   method: "GET|POST|DELETE|PUT|PATCH"
   *   baseUrl: "http://www.domain.com",
   *   url: "/api/v1/users",
   *   headers: {
   *     "Content-Type": "application/json"
   *  }
   * }).then((response: AxiosResponse<User>) => response.data)
   *
   */
  public request<T, R = AxiosResponse<T>>(config: AxiosRequestConfig): Promise<R> {
    return this.request(config);
  }

  /**
   * HTTP GET method, used to fetch data `statusCode`: 200.
   *
   * @access public
   * @template T - `TYPE`: expected object.
   * @template R - `RESPONSE`: expected object inside a axios response format.
   * @param {string} url - endpoint you want to reach.
   * @param {import("axios").AxiosRequestConfig} [config] - axios request configuration.
   * @returns {Promise<R>} HTTP `axios` response payload.
   * @memberof Api
   */
  public get<T, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
    return this.get(url, config);
  }

  /**
   * HTTP OPTIONS method.
   *
   * @access public
   * @template T - `TYPE`: expected object.
   * @template R - `RESPONSE`: expected object inside a axios response format.
   * @param {string} url - endpoint you want to reach.
   * @param {import("axios").AxiosRequestConfig} [config] - axios request configuration.
   * @returns {Promise<R>} HTTP `axios` response payload.
   * @memberof Api
   */
  public options<T, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
    return this.options(url, config);
  }

  /**
   * HTTP DELETE method, `statusCode`: 204 No Content.
   *
   * @access public
   * @template T - `TYPE`: expected object.
   * @template R - `RESPONSE`: expected object inside a axios response format.
   * @param {string} url - endpoint you want to reach.
   * @param {import("axios").AxiosRequestConfig} [config] - axios request configuration.
   * @returns {Promise<R>} - HTTP [axios] response payload.
   * @memberof Api
   */
  public delete<T, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
    return this.delete(url, config);
  }

  /**
   * HTTP HEAD method.
   *
   * @access public
   * @template T - `TYPE`: expected object.
   * @template R - `RESPONSE`: expected object inside a axios response format.
   * @param {string} url - endpoint you want to reach.
   * @param {import("axios").AxiosRequestConfig} [config] - axios request configuration.
   * @returns {Promise<R>} - HTTP [axios] response payload.
   * @memberof Api
   */
  public head<T, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
    return this.head(url, config);
  }

  /**
   * HTTP POST method `statusCode`: 201 Created.
   *
   * @access public
   * @template T - `TYPE`: expected object.
   * @template B - `BODY`: body request object.
   * @template R - `RESPONSE`: expected object inside a axios response format.
   * @param {string} url - endpoint you want to reach.
   * @param {B} data - payload to be send as the `request body`,
   * @param {import("axios").AxiosRequestConfig} [config] - axios request configuration.
   * @returns {Promise<R>} - HTTP [axios] response payload.
   * @memberof Api
   */
  public post<T, B, R = AxiosResponse<T>>(url: string, data?: B, config?: AxiosRequestConfig): Promise<R> {
    return this.post(url, data, config);
  }

  /**
   * HTTP PUT method.
   *
   * @access public
   * @template T - `TYPE`: expected object.
   * @template B - `BODY`: body request object.
   * @template R - `RESPONSE`: expected object inside a axios response format.
   * @param {string} url - endpoint you want to reach.
   * @param {B} data - payload to be send as the `request body`,
   * @param {import("axios").AxiosRequestConfig} [config] - axios request configuration.
   * @returns {Promise<R>} - HTTP [axios] response payload.
   * @memberof Api
   */
  public put<T, B, R = AxiosResponse<T>>(url: string, data?: B, config?: AxiosRequestConfig): Promise<R> {
    return this.put(url, data, config);
  }

  /**
   * HTTP PATCH method.
   *
   * @access public
   * @template T - `TYPE`: expected object.
   * @template B - `BODY`: body request object.
   * @template R - `RESPONSE`: expected object inside a axios response format.
   * @param {string} url - endpoint you want to reach.
   * @param {B} data - payload to be send as the `request body`,
   * @param {import("axios").AxiosRequestConfig} [config] - axios request configuration.
   * @returns {Promise<R>} - HTTP [axios] response payload.
   * @memberof Api
   */
  public patch<T, B, R = AxiosResponse<T>>(url: string, data?: B, config?: AxiosRequestConfig): Promise<R> {
    return this.patch(url, data, config);
  }

  // /**
  //  *
  //  * @template T - type.
  //  * @param {import("axios").AxiosResponse<T>} response - axios response.
  //  * @returns {T} - expected object.
  //  * @memberof Api
  //  */
  // public success<T>(response: AxiosResponse<T>): T {
  //   return response.data;
  // }

  // /**
  //  *
  //  *
  //  * @template T type.
  //  * @param {AxiosError<T>} error
  //  * @memberof Api
  //  */
  // public error<T>(error: AxiosError<T>): void {
  //   throw error;
  // }
}

export default Api;
